/**
 * Platform-supported locales — the closed set that the customer portal will
 * render strings + dates + currencies for. Mirror of the server-side
 * SUPPORTED_LOCALES allowlist in apps/api/src/routes/customer_profile.ts.
 *
 * en-GB is the source-of-truth for translations: every key in en-GB MUST
 * exist in every other locale. The TranslationKey union below is derived
 * from the en-GB catalogue so a typo at a callsite is a TypeScript error.
 *
 * Translation quality: en-US / es-US / fr-CA / de-DE strings are PROVISIONAL
 * best-effort translations. They should be reviewed by a native speaker
 * before any tenant in these markets goes live. The reactive bits work:
 * picker swaps the catalogue, dates + currencies reformat, Trans component
 * substitutes vars. The content quality is the v1.x polish.
 */

export const SUPPORTED_LOCALES = ['en-GB', 'en-US', 'es-US', 'fr-CA', 'de-DE'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export function isSupportedLocale(x: unknown): x is Locale {
  return typeof x === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(x);
}

export const DEFAULT_LOCALE: Locale = 'en-GB';

/**
 * Human-facing label for each locale, rendered by the locale picker.
 * Each label is in its OWN language so a user who doesn't read the
 * current portal locale can still recognise their preference.
 */
export const LOCALE_LABELS: Record<Locale, string> = {
  'en-GB': 'English (UK)',
  'en-US': 'English (US)',
  'es-US': 'Español (EE.UU.)',
  'fr-CA': 'Français (Canada)',
  'de-DE': 'Deutsch (Deutschland)',
};
